"use client";

/**
 * Free "Batter, step or support?" excavation selector
 * (/free/excavation-support-selector, SEO plan Workstream E row 1).
 *
 * Runs entirely in the browser: the answers live in React state only.
 * Nothing is fetched, stored or put in the URL, and the only analytics are
 * the fixed values in ./analytics.ts. The logic and every word of the result
 * are lib/marketing/excavation-selector/selector.ts, pinned path by path in
 * __tests__/lib/marketing/excavation-selector/selector.test.ts.
 */

import { useId, useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import {
  COMPETENT_PERSON_LINE,
  NOT_LEGAL_ADVICE,
  QUESTIONS,
  SCOPE_LINE,
  STATUS_LABELS,
  answerLabel,
  isComplete,
  selectApproaches,
  type Point,
  type SelectorAnswers,
} from "@/lib/marketing/excavation-selector/selector";
import { PRIVATE_REGION_CLASS, SECONDARY_BUTTON_CLASS } from "@/components/marketing/exposure-calculators/fields";
import { PRINT_BUTTON_CLASS, PrintRecordStyle } from "@/components/marketing/printable-record";
import { trackExcavationSelector } from "./analytics";

type Answers = Partial<SelectorAnswers>;

export const EXCAVATION_RECORD_ID = "excavation-selector-record";

function PointList({ points, testId }: { points: readonly Point[]; testId?: string }) {
  return (
    <ul data-testid={testId} className="mt-2 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-gray-700">
      {points.map((point) => (
        <li key={point.id} data-testid={`excavation-point-${point.id}`}>
          {point.text}{" "}
          <a
            href={point.cite.url}
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap font-semibold text-brand-700 underline hover:text-accent-dark"
          >
            ({point.cite.label})
          </a>
        </li>
      ))}
    </ul>
  );
}

export function ExcavationSelector() {
  const posthog = usePostHog();
  const baseId = useId();
  const usedReported = useRef(false);
  const completedReported = useRef(false);
  const [answers, setAnswers] = useState<Answers>({});

  const result = isComplete(answers) ? selectApproaches(answers) : null;

  function answer(id: keyof SelectorAnswers, value: string) {
    if (!usedReported.current) {
      usedReported.current = true;
      trackExcavationSelector(posthog, { event: "excavation_selector_used" });
    }
    const next = { ...answers, [id]: value } as Answers;
    setAnswers(next);
    if (!completedReported.current && isComplete(next)) {
      completedReported.current = true;
      trackExcavationSelector(posthog, { event: "excavation_selector_completed" });
    }
  }

  function print() {
    trackExcavationSelector(posthog, { event: "excavation_selector_printed" });
    window.print();
  }

  return (
    <section
      aria-labelledby={`${baseId}-heading`}
      data-testid="excavation-selector"
      data-clarity-mask="true"
      className={`${PRIVATE_REGION_CLASS} rounded-2xl border border-gray-200 bg-gray-50 p-5 sm:p-6`}
    >
      <PrintRecordStyle recordId={EXCAVATION_RECORD_ID} />
      <h2 id={`${baseId}-heading`} className="text-2xl font-bold text-gray-900">
        Batter, step or support?
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-gray-700">
        Answer six questions about the excavation. You will see which approaches are worth discussing with a
        competent person, and the points to raise. Your answers stay in your browser. Nothing you choose is sent to
        us.
      </p>

      <div className="mt-5 space-y-5 rounded-xl border border-gray-200 bg-white p-4">
        {QUESTIONS.map((question) => {
          const hintId = question.hint ? `${baseId}-${question.id}-hint` : undefined;
          return (
            <fieldset key={question.id} data-testid={`excavation-question-${question.id}`} aria-describedby={hintId}>
              <legend className="text-sm font-semibold text-gray-900">{question.label}</legend>
              {question.hint ? (
                <p id={hintId} className="mt-1 text-sm text-gray-600">
                  {question.hint}
                </p>
              ) : null}
              <div className="mt-2 space-y-2">
                {question.options.map((option) => (
                  <label key={option.value} className="flex items-start gap-3 text-sm text-gray-800">
                    <input
                      type="radio"
                      name={`${baseId}-${question.id}`}
                      value={option.value}
                      checked={answers[question.id] === option.value}
                      onChange={() => answer(question.id, option.value)}
                      className="mt-0.5 h-5 w-5 shrink-0 border-gray-300 text-accent focus:ring-accent"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          );
        })}
      </div>

      <div className="mt-4">
        <button type="button" onClick={() => setAnswers({})} className={SECONDARY_BUTTON_CLASS}>
          Start again
        </button>
      </div>

      <div aria-live="polite" data-testid="excavation-result" className="mt-5 rounded-xl border border-gray-200 bg-white p-4">
        {result === null ? (
          <p className="text-sm text-gray-600">Answer every question to see the approaches to discuss.</p>
        ) : (
          <>
            <p data-testid="excavation-competent-person" className="text-sm font-semibold text-gray-900">
              {COMPETENT_PERSON_LINE}
            </p>
            <h3 className="mt-4 text-lg font-bold text-gray-900">Approaches to discuss with a competent person</h3>
            <div className="mt-3 space-y-4">
              {result.approaches.map((approach) => (
                <div
                  key={approach.id}
                  data-testid={`excavation-approach-${approach.id}`}
                  data-status={approach.status}
                  className="rounded-lg border border-gray-200 p-3"
                >
                  <h4 className="text-base font-bold text-gray-900">{approach.title}</h4>
                  <p data-testid={`excavation-status-${approach.id}`} className="mt-1 text-sm font-semibold text-brand-700">
                    {STATUS_LABELS[approach.status]}
                  </p>
                  <PointList points={approach.points} />
                </div>
              ))}
            </div>
            <h3 className="mt-5 text-lg font-bold text-gray-900">Points to raise</h3>
            <PointList points={result.considerations} testId="excavation-considerations" />
            <h3 className="mt-5 text-lg font-bold text-gray-900">Whatever is chosen</h3>
            <PointList points={result.always} testId="excavation-always" />
            <p data-testid="excavation-scope" className="mt-5 text-sm leading-relaxed text-gray-700">
              {SCOPE_LINE} {NOT_LEGAL_ADVICE}
            </p>
            <div className="mt-4">
              <button type="button" onClick={print} className={PRINT_BUTTON_CLASS}>
                Print this record
              </button>
            </div>

            <div id={EXCAVATION_RECORD_ID} data-testid="excavation-record">
              <h2>Excavation: batter, step or support? Discussion record</h2>
              <p>Site or job: ____________________________ Date: ______________</p>
              <h3>Answers</h3>
              <ul>
                {QUESTIONS.map((question) => (
                  <li key={question.id}>
                    {question.label} {answerLabel(question, answers)}
                  </li>
                ))}
              </ul>
              <h3>Approaches to discuss</h3>
              <ul>
                {result.approaches.map((approach) => (
                  <li key={approach.id}>
                    {approach.title}: {STATUS_LABELS[approach.status]}
                    {approach.points.length > 0 && (
                      <ul>
                        {approach.points.map((point) => (
                          <li key={point.id}>
                            {point.text} ({point.cite.label})
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
              <h3>Points to raise</h3>
              <ul>
                {[...result.considerations, ...result.always].map((point) => (
                  <li key={point.id}>
                    {point.text} ({point.cite.label})
                  </li>
                ))}
              </ul>
              <p>{COMPETENT_PERSON_LINE}</p>
              <p>Approach agreed: ____________________________________________</p>
              <p>Competent person (name and role): ____________________________</p>
              <p>Signature: __________________________ Date: ______________</p>
              <p>
                {SCOPE_LINE} {NOT_LEGAL_ADVICE} Made with the free excavation selector at thesitebook.co.uk.
              </p>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
